import get from 'lodash/get.js';
import has from 'lodash/has.js';
import identity from 'lodash/identity.js';
import isEmpty from 'lodash/isEmpty.js';
import keys from 'lodash/keys.js';
import pickBy from 'lodash/pickBy.js';
import unset from 'lodash/unset.js';
import values from 'lodash/values.js';
import queryString from 'query-string';

import { handleResponseError } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import toastService from '@/services/ToastService.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export const filterOperators = {
    BETWEEN: 'BETWEEN',
    IN: 'IN',
};

/**
 * @param Collection
 * @param Item
 * @returns {*}
 */
export default function (Collection, Item) {
    return {
        namespaced: true,
        state: {
            loading: false,
            sort: {},
            paginate: {
                parameters: { limit: null, page: 1 },
                meta: {
                    total: null,
                    per_page: null,
                    current_page: 1,
                    last_page: null,
                },
            },
        },

        getters: {
            /**
             * @param state
             * @returns {*}
             */
            all(state) {
                return () => {
                    const items = { ...state };
                    unset(items, 'loading');
                    unset(items, 'sort');
                    unset(items, 'filter');
                    unset(items, 'paginate');

                    return items;
                };
            },

            /**
             * @param state
             * @param getters
             * @returns {function(): boolean}
             */
            isEmpty(state, getters) {
                return () => isEmpty(getters.all());
            },

            /**
             * @param state
             * @param getters
             * @returns {function(*): boolean}
             */
            has(state, getters) {
                return id => Boolean(getters.all()[id]);
            },

            /**
             * @param state
             * @param getters
             * @returns {function(*): *}
             */
            get(state, getters) {
                return id => getters.all()[id];
            },

            /**
             * @param state
             * @param getters
             * @returns {string}
             */
            queryString(state, getters) {
                const queries = [
                    getters.isSorting ? getters.sortingQueryString : null,
                    getters.isFiltering ? getters.filteringQueryString : null,
                    getters.canPaginate ? getters.paginateQueryString : null,
                ].filter(Boolean);

                return `?${queries.join('&')}`;
            },

            /**
             * @param state
             * @returns {*}
             */
            filtering(state) {
                return pickBy(state.filter, identity);
            },

            /**
             * @param state
             * @param getters
             * @return {boolean}
             */
            isFiltering(state, getters) {
                const { filtering } = getters;

                if (!filtering || isEmpty(filtering)) return false;

                return Object.keys(filtering).some(field => {
                    if (!has(filtering, field)) return false;

                    return Boolean(filtering[field].value) && Boolean(filtering[field].value.length);
                });
            },

            /**
             * @param state
             * @param getters
             * @return {Function}
             */
            isFilteringBy(state, getters) {
                return key => {
                    if (!getters.canFilterBy(key)) return false;

                    return Boolean(getters.filterValueBy(key)) && Boolean(getters.filterValueBy(key).length);
                };
            },

            /**
             * @param state
             * @param getters
             * @return {function(*=): boolean}
             */
            canFilterBy(state, getters) {
                return key => Boolean(keys(getters.filtering).includes(key));
            },

            /**
             * @param state
             * @param getters
             * @return {function(*): *}
             */
            filterValueBy(state, getters) {
                return key => getters.filtering[key].value;
            },

            /**
             * @param state
             * @param getters
             * @returns {string}
             */
            filteringQueryString(state, getters) {
                if (!getters.isFiltering || !state.filter || isEmpty(state.filter)) {
                    return '';
                }

                const filtering = {};

                Object.keys(state.filter).forEach(field => {
                    if (
                        has(state.filter, field) &&
                        Boolean(state.filter[field].value) &&
                        Boolean(state.filter[field].value.length)
                    ) {
                        filtering[field] = state.filter[field];
                    }
                });

                return queryString.stringify({
                    filter: JSON.stringify(filtering),
                });
            },

            /**
             * @param state
             * @param getters
             * @returns {*}
             */
            sorted(state, getters) {
                const items = Object.values(getters.all());

                // Find the active sort field and direction
                const sortField = Object.keys(state.sort).find(key => state.sort[key] !== null);
                const sortDirection = sortField ? state.sort[sortField] : null;

                if (!sortField || !sortDirection) {
                    return () => items; // Return unsorted if no sort criteria
                }

                // Helper function to normalize values for comparison
                const normalizeValue = (value, field) => {
                    // Handle null/undefined values
                    if (value === null || value === undefined) return '';

                    // Handle dates
                    if (field.includes('_at') || field.includes('date')) {
                        return new Date(value);
                    }

                    // Handle strings
                    if (typeof value === 'string') {
                        return value.toLowerCase();
                    }

                    return value;
                };

                return () =>
                    items.sort((a, b) => {
                        const aValue = normalizeValue(a[sortField], sortField);
                        const bValue = normalizeValue(b[sortField], sortField);

                        let comparison = 0;
                        if (aValue < bValue) {
                            comparison = -1;
                        } else if (aValue > bValue) {
                            comparison = 1;
                        }

                        // Apply sort direction
                        return sortDirection === 'desc' ? -comparison : comparison;
                    });
            },

            /**
             * @param state
             * @returns {*}
             */
            sorting(state) {
                return pickBy(state.sort, identity);
            },

            /**
             * @param state
             * @param getters
             * @returns {boolean}
             */
            isSorting(state, getters) {
                return !isEmpty(getters.sorting);
            },

            /**
             * @param state
             * @param getters
             * @returns {string}
             */
            sortingQueryString(state, getters) {
                const sort = JSON.stringify(getters.sorting);

                return getters.isSorting ? queryString.stringify({ sort }) : '';
            },

            /**
             * @param state
             * @returns {function(*): boolean}
             */
            canSortBy(state) {
                return key => Boolean(keys(state.sort).includes(key));
            },

            /**
             * @param state
             * @returns {{limit: null, page: number}|state.paginate|{limit, page}}
             */
            paginate(state) {
                return pickBy(state.paginate.parameters, identity);
            },

            /**
             * @param state
             * @returns {{}|state.paginate.meta}
             */
            pageMeta(state) {
                return state.paginate.meta;
            },

            /**
             * @param state
             * @param getters
             * @returns {number|null}
             */
            currentPage(state, getters) {
                return getters.canPaginate ? getters.pageMeta.current_page : null;
            },

            /**
             * @param state
             * @param getters
             * @returns {number|null}
             */
            pageCount(state, getters) {
                if (!getters.canPaginate) return null;

                return Math.ceil(getters.pageMeta.total / getters.pageMeta.per_page);
            },

            /**
             * @param state
             * @param getters
             * @returns {boolean}
             */
            canPaginate(state, getters) {
                return Boolean(state.paginate) && !isEmpty(getters.paginate);
            },

            /**
             * @param state
             * @param getters
             * @returns {string}
             */
            paginateQueryString(state, getters) {
                const { paginate } = getters;

                return `page=${paginate.page}`;
            },

            isLoading(state) {
                return state.loading;
            },
        },

        actions: {
            /**
             * @param context
             * @param id
             * @returns {Promise<void>}
             */
            async syncGroups(context, { id, modelKey }) {
                const item = context.getters.get(id);

                if (!item) {
                    return false;
                }

                const indicator = `${Collection}/updateGroups`;

                await context.dispatch('AutoSave/setSaving', indicator, { root: true });

                const result = await context.dispatch('updateGroups', {
                    subject: item,
                    groups: item.groups,
                    modelKey,
                });

                if (result instanceof Error) {
                    toastService.open({
                        message: 'Unable to save groups. Please check your connection and try again.',
                        type: 'is-danger',
                        position: 'is-bottom-left',
                    });

                    await context.dispatch('AutoSave/setError', { indicator, error: result }, { root: true });

                    return false;
                }

                await context.dispatch('AutoSave/setSaved', indicator, { root: true });

                return true;
            },

            /**
             * @param context
             * @param query
             * @returns {*}
             */
            fetchAll(context, query) {
                try {
                    const queryBuilder = new QueryBuilder(query.endpoint);

                    if (has(query, 'filters')) {
                        queryBuilder.filters(query.filters);
                    }

                    if (has(query, 'filter')) {
                        queryBuilder.filter(query.filter.key, query.filter.value);
                    }

                    if (has(query, 'include')) {
                        queryBuilder.include(query.include);
                    }

                    if (has(query, 'appends')) {
                        queryBuilder.append(query.appends);
                    }

                    if (has(query, 'raw')) {
                        queryBuilder.raw(query.raw);
                    }

                    const url = queryBuilder.toUrl();

                    return axios.get(url, { errorHandle: false });
                } catch (error) {
                    handleResponseError(error);
                    return Promise.reject(error);
                }
            },

            /**
             * @param context
             * @param items
             * @returns {Promise<any>}
             */
            hydrate(context, items) {
                return new Promise((resolve, reject) => {
                    Promise.all(
                        values(items).map((item, key) => {
                            if (item.order === undefined) item.order = key;

                            return context.dispatch('add', item);
                        })
                    )
                        .then(() => resolve())
                        .catch(reject);
                });
            },

            /**
             * @param context
             * @returns {Promise<any>}
             */
            purge(context) {
                return new Promise((resolve, reject) => {
                    const removePromises = values(context.getters.all())
                        .filter(item => item)
                        .map(item =>
                            context.dispatch(
                                'remove',
                                // TODO: Refactor so that id/uuid is flexible
                                `${item.uuid ? item.uuid : item.id}`
                            )
                        );

                    Promise.all(removePromises)
                        .then(() => resolve())
                        .catch(reject);
                });
            },

            /**
             * @param context
             * @param item
             * @returns {Promise<any>}
             */
            add(context, item) {
                return new Promise(resolve => {
                    const key = get(item, 'store_key', get(item, 'uuid', item.id));

                    if (!context.getters.has(key)) context.commit('ADD', item);

                    context.dispatch(`${key}/hydrate`, item).then(() => {
                        resolve();
                    });
                });
            },

            /**
             * @param context
             * @param id
             * @returns {Promise<any>}
             */
            remove(context, id) {
                return new Promise(resolve => {
                    context.commit('REMOVE', id);

                    resolve();
                });
            },

            /**
             * @param context
             * @param item
             * @returns {Promise<any>}
             */
            update(context, item) {
                return new Promise(resolve => {
                    context.dispatch(`${item.uuid ? item.uuid : item.id}/hydrate`, item);

                    resolve();
                });
            },

            /**
             * @param context
             * @param parameters
             * @returns {Promise<any>}
             */
            sort(context, parameters) {
                return new Promise(resolve => {
                    if (parameters && typeof parameters === 'object') {
                        Object.keys(parameters).forEach(param => {
                            context.commit('SORT', {
                                key: param,
                                value: parameters[param],
                            });
                        });
                    }

                    resolve();
                });
            },

            /**
             * @param context
             * @param params
             * @return {Promise<any>}
             */
            filter(context, params) {
                return new Promise(resolve => {
                    Object.keys(params).forEach(key => {
                        if (has(params, key)) {
                            context.commit('FILTER', { key, data: params[key] });
                        }
                    });

                    resolve();
                });
            },

            /**
             * @param context
             * @param params
             * @returns {Promise<any>}
             */
            updatePageParams(context, params) {
                return new Promise(resolve => {
                    if (params && typeof params === 'object') {
                        Object.keys(params).forEach(param => {
                            if (has(params, param)) {
                                context.commit('UPDATE_PAGE_PARAM', {
                                    key: param,
                                    value: params[param],
                                });
                            }
                        });
                    }

                    resolve();
                });
            },

            /**
             * @param context
             * @param meta
             * @returns {Promise<any>}
             */
            updatePageMeta(context, meta) {
                return new Promise(resolve => {
                    if (meta && typeof meta === 'object') {
                        Object.keys(meta).forEach(field => {
                            if (has(meta, field)) {
                                context.commit('UPDATE_PAGE_META', {
                                    key: field,
                                    value: meta[field],
                                });
                            }
                        });
                    }

                    resolve();
                });
            },

            /**
             * @param context
             * @param alert
             */
            throwError(context, alert) {
                context.dispatch(
                    'Alerts/add',
                    {
                        key: alert.key,
                        title: 'An Error Has Occurred',
                        message: alert.message,
                        type: 'danger',
                    },
                    { root: true }
                );
            },
        },

        mutations: {
            /**
             * @param state
             * @param item
             */
            ADD(state, item) {
                const key = get(item, 'store_key', get(item, 'uuid', item.id));

                this.registerModule([Collection, key], Item);
            },

            /**
             * @param state
             * @param id
             */
            REMOVE(state, id) {
                this.unregisterModule([Collection, id]);
            },

            /**
             * @param state
             * @param id
             */
            SORT(state, { key, value }) {
                state.sort[key] = value;
            },

            /**
             * @param state
             * @param key
             * @param data
             * @constructor
             */
            FILTER(state, { key, data }) {
                Object.keys(state.filter[key]).forEach(param => {
                    if (has(state.filter[key], param) && has(data, param)) {
                        state.filter[key][param] = data[param];
                    }
                });
            },

            /**
             * @param state
             * @param id
             */
            UPDATE_PAGE_PARAM(state, { key, value }) {
                state.paginate.parameters[key] = value;
            },

            /**
             * @param state
             * @param id
             */
            UPDATE_PAGE_META(state, { key, value }) {
                state.paginate.meta[key] = value;
            },

            /**
             * @constructor
             */
            TOGGLE_LOADING(state) {
                state.loading = !state.loading;
            },
        },
    };
}
